import React, { useState } from "react";
import { graphql, StaticQuery } from "gatsby";
import { RichText as PrismicRichText } from "prismic-reactjs";
import { lighten } from "polished";
import {
  withScriptjs,
  withGoogleMap,
  GoogleMap,
  Marker,
  InfoWindow,
} from "react-google-maps";

import { columnPush, fluidType, linkResolver, serializer } from "utils";
import {
  H1,
  PageHeader,
  Section,
  SEO,
  SplitImage,
  Text,
  Button,
  RichText,
  FadeGroup,
} from "components";

import Switcher from "./_switcher";
import MobileSwitcher from "./_mobileSwitcher";

const Map = withScriptjs(
  withGoogleMap(({ activeFilters, activeLocation, setActiveLocation }) => (
    <GoogleMap
      defaultZoom={14}
      defaultCenter={{ lat: 50.724104, lng: -113.975091 }}
      defaultOptions={{
        streetViewControl: false,
        navigationControl: false,
        mapTypeControl: false,
        backgroundColor: "#7CA1B7",
        mapTypeId: "roadmap",
        styles: [
          {
            featureType: "landscape",
            elementType: "geometry",
            stylers: [
              {
                color: "#9cb8c9",
              },
            ],
          },
          {
            featureType: "landscape",
            elementType: "labels",
            stylers: [
              {
                color: "#7CA1B7",
              },
            ],
          },
          {
            featureType: "poi",
            stylers: [
              {
                visibility: "off",
              },
            ],
          },
          {
            featureType: "poi",
            elementType: "geometry",
            stylers: [
              {
                color: "#7CA1B7",
              },
            ],
          },
          {
            featureType: "road",
            elementType: "geometry",
            stylers: [
              {
                color: "#ffffff",
              },
            ],
          },
          {
            featureType: "water",
            stylers: [
              {
                color: "#486d84",
              },
            ],
          },
        ],
      }}
    >
      {locations.map((location, index) => {
        if (activeFilters.indexOf(location[2]) !== -1) {
          return (
            <Marker
              key={"location" + index}
              position={{ lat: location[3], lng: location[4] }}
              icon={{
                url: location[6],
                scaledSize: new window.google.maps.Size(32, 32),
              }}
              onClick={() => {
                setActiveLocation(location[0]);
              }}
            >
              {activeLocation === location[0] && (
                <InfoWindow>
                  <span className="mapinfo">
                    <strong>{location[0]}</strong>
                    <br />
                    <small>
                      <a
                        href={
                          "https://maps.google.com?daddr=" +
                          location[7].replace(/ /g, "+")
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Directions
                      </a>
                    </small>
                    <br />
                    {location[1]}
                  </span>
                </InfoWindow>
              )}
            </Marker>
          );
        } else {
          return null;
        }
      })}
    </GoogleMap>
  ))
);

function AmenitiesPage({ preview }) {
  const [activeLocation, setActiveLocation] = useState("");
  const [activeFilters, setActiveFilters] = useState([
    "schools",
    "worship",
    "explore",
    "sports",
    "artsandculture",
    "foodanddrink",
  ]);

  const toggleActiveFilter = function (filter) {
    let activeFiltersNew = JSON.parse(JSON.stringify(activeFilters));
    if (activeFiltersNew.indexOf(filter) !== -1) {
      activeFiltersNew.splice(activeFiltersNew.indexOf(filter), 1);
    } else {
      activeFiltersNew.push(filter);
    }
    setActiveFilters(activeFiltersNew);
  };
  return (
    <StaticQuery
      query={graphql`
        {
          prismicAmenities {
            dataString
            data {
              main_image {
                fluid(maxWidth: 2048) {
                  ...GatsbyPrismicImageFluid
                }
              }
              features {
                feature_image {
                  fluid(maxWidth: 1600) {
                    ...GatsbyPrismicImageFluid
                  }
                }
              }
              footer_image {
                fluid(maxWidth: 2048) {
                  ...GatsbyPrismicImageFluid
                }
              }
              footer_alt_image {
                fluid(maxWidth: 2048) {
                  ...GatsbyPrismicImageFluid
                }
              }
            }
          }
        }
      `}
      render={(data) => {
        let page = JSON.parse(data.prismicAmenities.dataString);
        if (preview) {
          page = preview;
        }
        return (
          <>
            <SEO
              image={page.seo_image && page.seo_image.url}
              title={page.seo_title && PrismicRichText.asText(page.seo_title)}
              description={
                page.seo_description &&
                PrismicRichText.asText(page.seo_description)
              }
            />
            <PageHeader
              bg="brand.navy"
              src={preview ? page.main_image && page.main_image.url : undefined}
              fluid={
                preview
                  ? undefined
                  : data.prismicAmenities.data.main_image &&
                    data.prismicAmenities.data.main_image.fluid
              }
              alt={page.main_image && page.main_image.alt}
            />
            <Section pt={2} pb={3} bg={["brand.nickel", "bg.default"]}>
              <div
                css={`
                  -ms-grid-column: 3;
                  -ms-grid-column-span: 11;
                  grid-column: 2 / 8;
                  -ms-grid-row: 1;
                  grid-row: 1;
                  ${(props) => props.theme.mediaQueries.medium} {
                    -ms-grid-column: 1;
                    -ms-grid-column-span: 15;
                    grid-column: 1 / 9;
                  }
                  ${(props) => props.theme.mediaQueries.small} {
                    -ms-grid-column: 1;
                    -ms-grid-column-span: 7;
                    grid-column: 1 / 5;
                  }
                `}
              >
                <div
                  css={`
                    position: relative;
                    ${columnPush(1)};
                    ${(props) => props.theme.mediaQueries.small} {
                      padding-left: 0;
                    }
                  `}
                >
                  <FadeGroup>
                    <H1
                      children={
                        page.title && PrismicRichText.asText(page.title)
                      }
                      mb="0.5em"
                      color={["text.reverse", "text.default"]}
                    />
                    <RichText
                      children={
                        page.description &&
                        PrismicRichText.render(
                          page.description,
                          linkResolver,
                          serializer
                        )
                      }
                      color={["text.reverse", "text.default"]}
                    />
                  </FadeGroup>
                </div>
              </div>
            </Section>
            <Section
              pb={3}
              bg="bg.default"
              css={`
                ${(props) => props.theme.mediaQueries.small} {
                  padding-top: 15vw;
                }
              `}
            >
              <div
                css={`
                  display: flex;
                  flex-direction: column;
                  -ms-grid-column: 3;
                  -ms-grid-column-span: 19;
                  grid-column: 2 / 12;
                  -ms-grid-row: 1;
                  grid-row: 1;
                  ${(props) => props.theme.mediaQueries.medium} {
                    -ms-grid-column: 1;
                    -ms-grid-column-span: 15;
                    grid-column: 1 / 9;
                  }
                  ${(props) => props.theme.mediaQueries.small} {
                    -ms-grid-column: 1;
                    -ms-grid-column-span: 7;
                    grid-column: 1 / 5;
                  }
                `}
              >
                <svg
                  viewBox="0 0 39 37"
                  css={`
                    width: 1em;
                    height: 1em;
                    margin: 0 auto;
                    ${fluidType(3)};
                    vertical-align: top;
                  `}
                >
                  <title>Star</title>
                  <path
                    d="M20 .61l3 9.11a5.16 5.16 0 0 0 4.88 3.55h9.52a.87.87 0 0 1 .88.88.9.9 0 0 1-.36.72l-7.75 5.63a5.11 5.11 0 0 0-1.86 5.73l3 9.11a.89.89 0 0 1-1.37.99l-7.79-5.63a5.1 5.1 0 0 0-6 0l-7.77 5.63a.88.88 0 0 1-1.36-1l3-9.11a5.13 5.13 0 0 0-1.91-5.72L.37 14.87a.88.88 0 0 1-.2-1.24.84.84 0 0 1 .72-.36h9.58a5.14 5.14 0 0 0 4.87-3.55l3-9.11A.89.89 0 0 1 19.42 0a.86.86 0 0 1 .58.61z"
                    fill="#707070"
                  />
                </svg>
                <H1
                  children={
                    page.discover_title &&
                    PrismicRichText.asText(page.discover_title)
                  }
                  mt="0.5em"
                  mb="2em"
                  textAlign="center"
                  color="brand.nickel"
                />
                <div
                  css={`
                    ${(props) => props.theme.mediaQueries.small} {
                      display: none;
                    }
                  `}
                >
                  <Switcher page={page} data={data} preview={preview} />
                </div>
                <div
                  css={`
                    display: none;
                    ${(props) => props.theme.mediaQueries.small} {
                      display: block;
                    }
                  `}
                >
                  <MobileSwitcher page={page} data={data} preview={preview} />
                </div>
              </div>
            </Section>
            <Section
              pb={3}
              css={`
                ${(props) => props.theme.mediaQueries.small} {
                  padding-bottom: 0;
                }
              `}
            >
              <div
                css={`
                  display: flex;
                  flex-direction: column;
                  -ms-grid-column: 1;
                  -ms-grid-column-span: 23;
                  grid-column: 1 / 13;
                  -ms-grid-row: 1;
                  grid-row: 1;
                  ${(props) => props.theme.mediaQueries.medium} {
                    -ms-grid-column: 1;
                    -ms-grid-column-span: 15;
                    grid-column: 1 / 9;
                  }
                  ${(props) => props.theme.mediaQueries.small} {
                    -ms-grid-column: 1;
                    -ms-grid-column-span: 7;
                    grid-column: 1 / 5;
                  }
                `}
              >
                <H1
                  children={
                    page.amenities_title &&
                    PrismicRichText.asText(page.amenities_title)
                  }
                  my="0.5em"
                  textAlign="center"
                />
                <RichText
                  children={
                    page.amenities_description &&
                    PrismicRichText.render(
                      page.amenities_description,
                      linkResolver,
                      serializer
                    )
                  }
                  textAlign="center"
                  mb="2em"
                />
                <div
                  css={`
                    padding-bottom: 5vw;
                    text-align: center;
                    > button {
                      width: auto !important;
                      width: initial !important;
                      padding-top: 1em;
                      padding-bottom: 1em;
                      margin-bottom: 0.5em;
                      &:not(:last-of-type) {
                        margin-right: 0.5em;
                      }
                    }
                  `}
                >
                  <Button
                    children={`Arts & Entertainment`}
                    onClick={() => {
                      toggleActiveFilter("artsandculture");
                    }}
                    className={
                      activeFilters.indexOf("artsandculture") !== -1
                        ? "active"
                        : "inactive"
                    }
                    variant="peach"
                  />
                  <Button
                    children={`Explore`}
                    onClick={() => {
                      toggleActiveFilter("explore");
                    }}
                    className={
                      activeFilters.indexOf("explore") !== -1
                        ? "active"
                        : "inactive"
                    }
                    variant="peach"
                  />
                  <Button
                    children={`Food & Drink`}
                    onClick={() => {
                      toggleActiveFilter("foodanddrink");
                    }}
                    className={
                      activeFilters.indexOf("foodanddrink") !== -1
                        ? "active"
                        : "inactive"
                    }
                    variant="peach"
                  />
                  <Button
                    children={`Schools`}
                    onClick={() => {
                      toggleActiveFilter("schools");
                    }}
                    className={
                      activeFilters.indexOf("schools") !== -1
                        ? "active"
                        : "inactive"
                    }
                    variant="peach"
                  />
                  <Button
                    children={`Sports`}
                    onClick={() => {
                      toggleActiveFilter("sports");
                    }}
                    className={
                      activeFilters.indexOf("sports") !== -1
                        ? "active"
                        : "inactive"
                    }
                    variant="peach"
                  />
                  <Button
                    children={`Worship`}
                    onClick={() => {
                      toggleActiveFilter("worship");
                    }}
                    className={
                      activeFilters.indexOf("worship") !== -1
                        ? "active"
                        : "inactive"
                    }
                    variant="peach"
                  />
                </div>
                <div
                  css={`
                    margin: 0 -5vw;
                    ${(props) => props.theme.mediaQueries.small} {
                      margin: 0 -10vw;
                    }
                  `}
                >
                  <Map
                    activeFilters={activeFilters}
                    activeLocation={activeLocation}
                    setActiveLocation={setActiveLocation}
                    googleMapURL="https://maps.googleapis.com/maps/api/js?key=AIzaSyDF7iuuNqggSLP-QCK5bvv55RNN-Z1D0oo&v=3.exp&libraries=geometry,drawing,places"
                    loadingElement={<div style={{ height: `100%` }} />}
                    containerElement={<div style={{ height: `85vh` }} />}
                    mapElement={<div style={{ height: `100%` }} />}
                  />
                </div>
              </div>
            </Section>
            <FadeGroup>
              <SplitImage
                src={
                  preview
                    ? page.footer_image && page.footer_image.url
                    : undefined
                }
                fluid={
                  preview
                    ? undefined
                    : data.prismicAmenities.data.footer_image &&
                      data.prismicAmenities.data.footer_image.fluid
                }
                alt={page.footer_image && page.footer_image.alt}
                bg="brand.nickel"
              />
            </FadeGroup>
            <Section pb={2} bg="brand.nickel">
              <div
                css={`
                  -ms-grid-column: 3;
                  -ms-grid-column-span: 9;
                  grid-column: 2 / 7;
                  -ms-grid-row: 1;
                  grid-row: 1;
                  ${(props) => props.theme.mediaQueries.medium} {
                    -ms-grid-column: 1;
                    -ms-grid-column-span: 15;
                    grid-column: 1 / 9;
                    grid-row: 2;
                  }
                  ${(props) => props.theme.mediaQueries.small} {
                    -ms-grid-column: 1;
                    -ms-grid-column-span: 7;
                    grid-column: 1 / 5;
                  }
                `}
              >
                <div
                  css={`
                    position: relative;
                    ${(props) => props.theme.mediaQueries.small} {
                      padding-left: 0;
                    }
                  `}
                >
                  <FadeGroup>
                    <H1
                      children={
                        page.footer_title &&
                        PrismicRichText.asText(page.footer_title)
                      }
                      mb="0.5em"
                      color="text.reverse"
                    />
                    <Text
                      children={
                        page.footer_description &&
                        PrismicRichText.render(
                          page.footer_description,
                          linkResolver,
                          serializer
                        )
                      }
                      color="text.reverse"
                    />
                  </FadeGroup>
                </div>
              </div>
              <div
                css={`
                  -ms-grid-column: 15;
                  -ms-grid-column-span: 10;
                  grid-column: 8 / 12;
                  -ms-grid-row: 1;
                  grid-row: 1;
                  display: -webkit-box;
                  display: -ms-flexbox;
                  display: flex;
                  ${(props) => props.theme.mediaQueries.medium} {
                    -ms-grid-column: 3;
                    -ms-grid-column-span: 13;
                    grid-column: 2 / 8;
                    -ms-grid-row: 1;
                    grid-row: 1;
                  }
                  ${(props) => props.theme.mediaQueries.small} {
                    -ms-grid-column: 1;
                    -ms-grid-column-span: 7;
                    grid-column: 1 / 5;
                  }
                  ${(props) =>
                    props.theme.mediaQueries
                      .medium} and (-ms-high-contrast: none),
                (-ms-high-contrast: active) {
                    margin-top: 5vw;
                  }
                  ${(props) =>
                    props.theme.mediaQueries
                      .small} and (-ms-high-contrast: none),
                (-ms-high-contrast: active) {
                    margin-top: 10vw;
                  }
                `}
              >
                <div
                  css={`
                    width: 100%;
                    margin-top: auto;
                  `}
                >
                  <img
                    src="/svg/community-pathway.svg"
                    css={`
                      width: 100%;
                      vertical-align: top;
                    `}
                  />
                </div>
              </div>
            </Section>
            <FadeGroup>
              <SplitImage
                src={
                  preview
                    ? page.footer_alt_image && page.footer_alt_image.url
                    : undefined
                }
                fluid={
                  preview
                    ? undefined
                    : data.prismicAmenities.data.footer_alt_image &&
                      data.prismicAmenities.data.footer_alt_image.fluid
                }
                alt={page.footer_alt_image && page.footer_alt_image.alt}
                bg="brand.navy"
                topBg="brand.nickel"
              />
            </FadeGroup>
          </>
        );
      }}
    />
  );
}

export default AmenitiesPage;

let locations = [
  [
    "Strathcona-Tweedsmuir School K-12 (Private)",
    "",
    "schools",
    50.775114952917,
    -114.03082297819822,
    4,
    `/map-pins/school.png`,
    "Range Rd 11A, De Winton, AB T0L 0X0, Canada",
  ],

  [
    "Foothills Composite High School 10-12 (Public)",
    "",
    "schools",
    50.71467699999999,
    -113.97250500000001,
    4,
    `/map-pins/school.png`,
    "229 Woodhaven Dr T1S 2A7",
  ],

  [
    "St. Luke’s Outreach School",
    "",
    "schools",
    50.7257894,
    -113.97626609999998,
    4,
    `/map-pins/school.png`,
    "22 Elizabeth St. T1S 1J7",
  ],

  [
    "Edison School K-12 (Private)",
    "",
    "schools",
    50.752589,
    -113.97693099999998,
    4,
    `/map-pins/school.png`,
    "322155 15th St East T1S 1A2",
  ],

  [
    "Holy Trinity Academy 10 – 12 (Catholic)",
    "",
    "schools",
    50.7406128,
    -113.9493167,
    4,
    `/map-pins/school.png`,
    "338072 – 32St East, RR2 T1S 1A2",
  ],

  [
    "Ecole Beausoleil Pre K – 9 (Public)",
    "",
    "schools",
    50.7297972,
    -113.9682631,
    4,
    `/map-pins/school.png`,
    "71 Okotoks Dr T1S 1G2",
  ],

  [
    "St. John Paul II Collegiate 7-9 (Catholic)",
    "",
    "schools",
    50.711839,
    -113.96850799999999,
    4,
    `/map-pins/school.png`,
    "53 Cimarron Drive T1S 2A6",
  ],

  [
    "Westmount School K – 9",
    "",
    "schools",
    50.707209,
    -113.979917,
    4,
    `/map-pins/school.png`,
    " 240 Westland St T1S 2J5",
  ],

  [
    "Ecole Okotoks JR. High (Public)",
    "",
    "schools",
    50.7269701,
    -113.97001080000001,
    4,
    `/map-pins/school.png`,
    "1 Pacific Ave T1S 2A9",
  ],

  [
    "Open Arms Catholic Preschool",
    "",
    "schools",
    50.7277449,
    -113.97031040000002,
    4,
    `/map-pins/school.png`,
    "33 Knight St. T1S 1G3",
  ],

  [
    "Leap n’ Learn Preschool",
    "",
    "schools",
    50.73186399999999,
    -113.95094890000001,
    4,
    `/map-pins/school.png`,
    "103 – 235 Milligan Dr. T1S 0B8",
  ],

  [
    "Okotoks Preschool Academy",
    "",
    "schools",
    50.7223671,
    -113.95775989999999,
    4,
    `/map-pins/school.png`,
    "#108 – 14 Crystal Ridge Dr. T1S 2C3",
  ],

  [
    "Okotoks Montessori Preschool",
    "",
    "schools",
    50.719554,
    -113.98365460000002,
    4,
    `/map-pins/school.png`,
    "1300 Village Ln T1S1Z6",
  ],

  [
    "St. Francis of Assisi Academy K-7 (Catholic)",
    "",
    "schools",
    50.804979,
    -113.94898360000002,
    4,
    `/map-pins/school.png`,
    "29218-29226 274 Ave, De Winton, AB T0L 0X0",
  ],

  [
    "St. Mary’s School K-6 (Catholic)",
    "",
    "schools",
    50.709643,
    -113.96545700000001,
    4,
    `/map-pins/school.png`,
    "42 Cimmaron Trail T1S 2A8",
  ],

  [
    "Ecole Good Shepherd School K-6 (Catholic)",
    "",
    "schools",
    50.7364031,
    -113.96883660000003,
    4,
    `/map-pins/school.png`,
    "52 Robinson Drive T1S 2A3",
  ],

  [
    "Big Rock School Pre-K-6 (Public)",
    "",
    "schools",
    50.719081,
    -113.98546199999998,
    4,
    `/map-pins/school.png`,
    "33 Hunters Gate T1S 2A4",
  ],

  [
    "Ecole Percy Pegler School (Public)",
    "",
    "schools",
    50.7295281,
    -113.9681938,
    4,
    `/map-pins/school.png`,
    "69 Okotoks Dr. T1S 2B1",
  ],

  [
    "Dr. Morris Gibson School K-6 (Public)",
    "",
    "schools",
    50.731435,
    -113.956458,
    4,
    `/map-pins/school.png`,
    "147 Crystal Ridge Dr. T1S 2B2",
  ],

  [
    "Calgary Polo Club",
    "",
    "sports",
    50.77204917870283,
    -114.0390944480896,
    4,
    "/map-pins/sports.png",
    "24041 306 Ave W, Okotoks, AB T1S 1A2, Canada",
  ],

  [
    "Kayben Farms",
    "",
    "explore",
    50.7659668,
    -113.94891150000001,
    4,
    "/map-pins/parks.png",
    "316034 32 St E",
  ],

  [
    "Calgary Firearms Centre and Conservation Education Centre for Excellence",
    "",
    "explore",
    50.8339629,
    -114.04785720000001,
    4,
    "/map-pins/parks.png",
    "242 Avenue & 32 St, De Winton, AB T0L 0X0",
  ],

  [
    "Cross Conservation Area",
    "",
    "explore",
    50.876008,
    -114.23431099999999,
    4,
    "/map-pins/parks.png",
    "194001 160 St W",
  ],

  [
    "Leighton Art Centre",
    "",
    "artsandculture",
    50.7963184,
    -114.2101065,
    4,
    "/map-pins/events.png",
    "282027 144 St W, Millarville, AB T0L 1K0",
  ],

  [
    "Church of Jesus Christ of Latter Day Saints",
    "",
    "worship",
    50.7162053,
    -113.97914220000001,
    4,
    "/map-pins/worship.png",
    "Church Of Jesus Christ Of Latter-day Saints, Woodgate Road, Okotoks, AB, Canada",
  ],

  [
    "United Church of Canada",
    "",
    "worship",
    50.7243722,
    -113.97961620000001,
    4,
    "/map-pins/worship.png",
    "United Church Of Canada, Riverside Drive, Okotoks, AB, Canada",
  ],

  [
    "Trinity Mennonite Church",
    "",
    "worship",
    50.842658,
    -114.0507,
    4,
    "/map-pins/worship.png",
    "Trinity Mennonite Church, Foothills No. 31, AB, Canada",
  ],

  [
    "St. Peter&#8217;s Anglican Church",
    "",
    "worship",
    50.7240997,
    -113.9832715,
    4,
    "/map-pins/worship.png",
    "Riverside Way, Okotoks, AB, Canada",
  ],

  [
    "St. James Catholic Church/Youth Program",
    "",
    "worship",
    50.7440117,
    -113.9506624,
    4,
    "/map-pins/worship.png",
    "St. James Church, 32 Street East, De Winton, AB, Canada",
  ],

  [
    "Okotoks Evangelical Free Church",
    "",
    "worship",
    50.7138228,
    -113.97884850000003,
    4,
    "/map-pins/worship.png",
    "Okotoks Evangelical Free Church, Westland Road, Okotoks, AB, Canada",
  ],

  [
    "Okotoks Alliance Church",
    "",
    "worship",
    50.7508635,
    -113.97678919999998,
    4,
    "/map-pins/worship.png",
    "Okotoks Alliance Church, Okotoks, AB, Canada",
  ],

  [
    "Holy Cross Lutheran Church",
    "",
    "worship",
    50.725171,
    -113.98283670000001,
    4,
    "/map-pins/worship.png",
    "Holy Cross Lutheran Church, Riverside Drive, Okotoks, AB, Canada",
  ],

  [
    "Grace Lutheran Church/Youth Program",
    "",
    "worship",
    50.7336023,
    -113.96745770000001,
    4,
    "/map-pins/worship.png",
    "Grace Lutheran Church, Waldron Avenue, Okotoks, AB, Canada",
  ],

  [
    "Foothills Community Church",
    "",
    "worship",
    50.72535480000001,
    -113.97627620000003,
    4,
    "/map-pins/worship.png",
    "Foothills Community Church of Okotoks, Elizabeth Street, Okotoks, AB, Canada",
  ],

  [
    "Big Rock Baptist Church",
    "",
    "worship",
    50.731435,
    -113.956458,
    4,
    "/map-pins/worship.png",
    "Big Rock Baptist Church, Crystal Ridge Drive, Okotoks, AB, Canada",
  ],

  [
    "Longview",
    "",
    "explore",
    50.5313416,
    -114.23338749999999,
    4,
    "/map-pins/parks.png",
    "Longview, AB, Canada",
  ],

  [
    "High River",
    "",
    "explore",
    50.5801021,
    -113.87073120000002,
    4,
    "/map-pins/parks.png",
    "High River, AB, Canada",
  ],

  [
    "Granary Road",
    "",
    "explore",
    50.8448415,
    -114.16115000000002,
    4,
    "/map-pins/parks.png",
    "Granary Road, Foothills No. 31, AB, Canada",
  ],

  [
    "Millarville",
    "",
    "explore",
    50.75663110000001,
    -114.32587610000002,
    4,
    "/map-pins/parks.png",
    "Millarville, AB, Canada",
  ],

  [
    "Black Diamond/Turner Valley",
    "",
    "explore",
    50.68838649999999,
    -114.23264419999998,
    4,
    "/map-pins/parks.png",
    "Black Diamond, AB, Canada",
  ],

  [
    "Big Rock",
    "",
    "explore",
    50.702841,
    -114.14574540000001,
    4,
    "/map-pins/parks.png",
    "AB-7, Black Diamond, Foothills No. 31, AB T0L 0H0, Canada",
  ],

  [
    "Okotoks Oilers",
    "",
    "sports",
    50.7169428,
    -113.97559999999999,
    4,
    "/map-pins/sports.png",
    "Pason Centennial Arena, Okotoks, AB, Canada",
  ],

  [
    "Okotoks Dawgs",
    "",
    "sports",
    50.7189624,
    -113.94715830000001,
    4,
    "/map-pins/sports.png",
    "Seaman Stadium, Don Seaman Way, Okotoks, AB, Canada",
  ],

  [
    "Crescent Point Field House",
    "",
    "sports",
    50.6915881,
    -113.90225350000003,
    4,
    "/map-pins/sports.png",
    "Crescent Point Regional Field House, Aldersyde, AB, Canada",
  ],

  [
    "Okotoks Recreation Centre",
    "",
    "sports",
    50.73058609999999,
    -113.965195,
    4,
    "/map-pins/sports.png",
    "Okotoks Recreation Centre, Okotoks Drive, Okotoks, AB, Canada",
  ],

  [
    "Skate Park",
    "",
    "sports",
    50.731783,
    -113.96697499999999,
    4,
    "/map-pins/sports.png",
    "75-99 Okotoks Dr, Okotoks, AB T1S, Canada",
  ],

  [
    "The George Public House",
    "",
    "foodanddrink",
    50.7160231,
    -113.97740199999998,
    4,
    "/map-pins/restaurants.png",
    "The George, Southridge Drive, Okotoks, AB, Canada",
  ],

  [
    "The Duke Pub",
    "",
    "foodanddrink",
    50.72569180000001,
    -113.97522019999997,
    4,
    "/map-pins/restaurants.png",
    "2 Elizabeth St, Okotoks, AB T1S 1J7, Canada",
  ],

  [
    "Homeground Coffee &#038; Roasting House",
    "",
    "foodanddrink",
    50.7251208,
    -113.97053289999997,
    4,
    "/map-pins/restaurants.png",
    "Home Ground Coffee & Roasting House, North Railway Street, Okotoks, AB, Canada",
  ],

  [
    "Sweet Grass Deli",
    "",
    "foodanddrink",
    50.725438,
    -113.97805299999999,
    4,
    "/map-pins/restaurants.png",
    "Sweetgrassdeli & Eatery, Elizabeth Street, Okotoks, AB, Canada",
  ],

  [
    "Heartland Café",
    "",
    "foodanddrink",
    50.7255769,
    -113.97147970000003,
    4,
    "/map-pins/restaurants.png",
    "Heartland Cafe, McRae Street, Okotoks, AB, Canada",
  ],

  [
    "Cozy Cottage Interiors",
    "",
    "artsandculture",
    50.725309,
    -113.97175800000002,
    4,
    "/map-pins/events.png",
    "Cozy Cottage Interiors, McRae Street, Okotoks, AB, Canada",
  ],

  [
    "Jazmine Harbour",
    "",
    "artsandculture",
    50.726031,
    -113.97992399999998,
    4,
    "/map-pins/events.png",
    "88 Elizabeth St, Okotoks, AB T1S 1J9, Canada",
  ],

  [
    "Tribal Connections",
    "",
    "artsandculture",
    50.72511799999999,
    -113.97205589999999,
    4,
    "/map-pins/events.png",
    "Tribal Connection Market, McRae Street, Okotoks, AB, Canada",
  ],

  [
    "Dale Gallery",
    "",
    "artsandculture",
    50.7250381,
    -113.9717602,
    4,
    "/map-pins/events.png",
    "Dalë Gallery, McRae Street, Okotoks, AB, Canada",
  ],

  [
    "Boothill Gallery",
    "",
    "artsandculture",
    50.72534769999999,
    -113.97774040000002,
    4,
    "/map-pins/events.png",
    "Boot Hill Gallery & Gift Shop, Okotoks, AB, Canada",
  ],

  [
    "Okotoks Art Gallery",
    "",
    "artsandculture",
    50.7239744,
    -113.96814670000003,
    4,
    "/map-pins/events.png",
    "Okotoks Art Gallery, North Railway Street, Okotoks, AB, Canada",
  ],
];
